export interface StartNovelSessionRequest {
  title?: string;
  concept?: string;
  genre?: string;
  style?: string;
  targetAudience?: string;
  targetWordCount?: number;
  chapterCount?: number;
  pov?: string;
  tense?: string;
  additionalInstructions?: string;
  maxTokensPerTurn?: number;
}

export interface NovelSessionStartResponse {
  success: boolean;
  errors?: string[];
  sessionId?: string;
  message?: string;
  title?: string;
  overview?: string;
  chapterOutlines?: ChapterOutline[] | null;
  initialOutline?: NovelOutline;
}

export interface GenerateNovelOutlineRequest {
  includeCharacterDescriptions?: boolean;
  includeLocationDescriptions?: boolean;
  includeThemes?: boolean;
  includeSubplots?: boolean;
  detailLevel?: string;
  maxTokens?: number;
}

export interface NovelOutlineResponse {
  success: boolean;
  errors?: string[];
  sessionId?: string;
  title?: string;
  overview?: string;
  premise?: string;
  themes?: string[];
  mainCharacters?: NovelCharacter[];
  supportingCharacters?: NovelCharacter[];
  locations?: NovelLocation[];
  chapters?: ChapterOutline[];
  subplots?: string[];
}

export interface CreateCharacterProfileRequest {
  name?: string;
  role?: string;
  description?: string;
  backstory?: string;
  personality?: string;
  relationships?: string;
  arc?: string;
  additionalDetails?: string;
  maxTokens?: number;
}

export interface CreateLocationRequest {
  name?: string;
  type?: string;
  description?: string;
  significance?: string;
  atmosphere?: string;
  details?: string;
  maxTokens?: number;
}

export interface GenerateNovelChapterRequest {
  chapterNumber: number;
  chapterTitle?: string;
  chapterSummary?: string;
  charactersPresent?: string[];
  locationsPresent?: string[];
  previousChaptersSummary?: string;
  specificInstructions?: string;
  targetWordCount?: number;
  maxTokens?: number;
}

export interface NovelChapterResponse {
  success: boolean;
  errors?: string[];
  sessionId?: string;
  chapter?: NovelChapter;
}

export interface NovelChapter {
  chapterNumber: number;
  title?: string;
  summary?: string;
  content?: string;
  charactersPresent?: string[];
  locationsPresent?: string[];
  wordCount?: number;
  createdAt?: string;
}

export interface NovelCharacter {
  name?: string;
  description?: string;
  role?: string;
  personality?: string;
  backstory?: string;
  relationships?: string;
  characterArc?: string;
  firstAppearance?: number;
}

export interface NovelLocation {
  name?: string;
  type?: string;
  description?: string;
  atmosphere?: string;
  significance?: string;
  firstAppearance?: number;
}

export interface ChapterOutline {
  chapterNumber: number;
  title?: string;
  summary?: string;
  keyEvents?: string[];
  charactersPresent?: string[];
  locationsPresent?: string[];
  plotProgression?: string;
}

export interface NovelOutline {
  title?: string;
  premise?: string;
  themes?: string[];
  chapters?: ChapterOutline[];
}

export interface CharacterProfileResponse {
  success: boolean;
  errors?: string[];
  sessionId?: string;
  character?: NovelCharacter;
}

export interface LocationResponse {
  success: boolean;
  errors?: string[];
  sessionId?: string;
  location?: NovelLocation;
}

export interface NovelSessionStatusResponse {
  success: boolean;
  errors?: string[];
  sessionId?: string;
  title?: string;
  concept?: string;
  genre?: string;
  outline?: NovelOutline;
  characters?: NovelCharacter[];
  locations?: NovelLocation[];
  chaptersWritten?: number;
  totalChapters?: number;
  currentWordCount?: number;
  targetWordCount?: number;
  createdAt?: string;
  lastUpdated?: string;
}

// Local storage models
export interface NovelSession {
  id: string;
  sessionId?: string;
  title: string;
  concept?: string;
  genre?: string;
  style?: string;
  theme?: string;
  targetAudience?: string;
  synopsis?: string;
  targetWordCount?: number;
  currentWordCount?: number;
  outline?: NovelOutline;
  characters: NovelCharacter[];
  locations: NovelLocation[];
  chapters: NovelChapter[];
  metadata?: Record<string, any>;
  createdAt: Date;
  updatedAt?: Date;
}

export interface NovelSessionStorage {
  sessions: NovelSession[];
  lastUpdated: Date;
}

// Constants
export const NOVEL_GENRES = [
  'Fantasy',
  'Science Fiction',
  'Mystery',
  'Thriller',
  'Romance',
  'Historical Fiction',
  'Literary Fiction',
  'Horror',
  'Young Adult',
  'Dystopian',
  'Crime',
  'Adventure',
  'Magical Realism',
  'Contemporary Fiction',
  'Psychological Thriller'
];

export const WRITING_STYLES = [
  'Literary',
  'Commercial',
  'Minimalist',
  'Descriptive',
  'Fast-paced',
  'Character-driven',
  'Plot-driven',
  'Atmospheric',
  'Humorous',
  'Dark',
  'Lyrical',
  'Conversational'
];

export const POV_OPTIONS = [
  'First Person',
  'Third Person Limited',
  'Third Person Omniscient',
  'Second Person',
  'Multiple POV'
];

export const TENSE_OPTIONS = [
  'Past Tense',
  'Present Tense',
  'Mixed Tense'
];

export const CHARACTER_ROLES = [
  'Protagonist',
  'Antagonist',
  'Love Interest',
  'Mentor',
  'Sidekick',
  'Rival',
  'Supporting Character',
  'Comic Relief',
  'Mysterious Figure',
  'Authority Figure'
];

export const LOCATION_TYPES = [
  'City',
  'Town',
  'Village',
  'Forest',
  'Mountain',
  'Desert',
  'Island',
  'Castle',
  'Spaceship',
  'Planet',
  'Building',
  'Underground',
  'Underwater',
  'Magical Realm',
  'Virtual World'
];